import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

interface Options {
  width: number;
  align?: 'left' | 'right';
  gap?: number;
  itemSelector?: string;
}

interface AnchoredMenu {
  open: boolean;
  toggle: () => void;
  close: (opts?: { focusAnchor?: boolean }) => void;
  anchorRef: RefObject<HTMLButtonElement>;
  menuRef: RefObject<HTMLDivElement>;
  popupStyle: CSSProperties | null;
}

/**
 * The shared behaviour behind every `.brief-menu` popup: right-aligned by
 * default under its trigger, portalled to <body> so it escapes the header's stacking
 * context, arrow keys to walk it, Escape back to the trigger.
 *
 * Disabled items stay in the arrow-key cycle on purpose — their whole job is
 * to explain why they are disabled, which nobody can read if focus skips them.
 */
export function useAnchoredMenu({ width, align = 'right', gap = 6, itemSelector = '.brief-menu__item' }: Options): AnchoredMenu {
  const [open, setOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  function position() {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const left = Math.max(8, Math.min(align === 'left' ? r.left : r.right - width, window.innerWidth - width - 8));
    setPopupStyle({ position: 'fixed', top: r.bottom + gap, left, width, zIndex: 60 });
  }

  function toggle() {
    if (!open) position();
    setOpen((v) => !v);
  }

  function close(opts?: { focusAnchor?: boolean }) {
    setOpen(false);
    if (opts?.focusAnchor) anchorRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    // Focus the first item so the menu is reachable from the keyboard the
    // moment it opens, like the field menus.
    menuRef.current?.querySelector<HTMLButtonElement>(itemSelector)?.focus();
  }, [open, itemSelector]);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        anchorRef.current?.focus();
        return;
      }
      if (e.key === 'Tab') {
        // Hand focus to the anchor first, so the Tab itself lands on whatever
        // follows the anchor rather than restarting from the top of the page.
        anchorRef.current?.focus();
        setOpen(false);
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const items = Array.from(
        menuRef.current?.querySelectorAll<HTMLButtonElement>(itemSelector) ?? [],
      );
      if (items.length === 0) return;
      e.preventDefault();
      const i = items.findIndex((b) => b === document.activeElement);
      const next =
        e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
      items[next]?.focus();
    }
    function onReposition() {
      position();
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, itemSelector]);

  return { open, toggle, close, anchorRef, menuRef, popupStyle };
}
